import './SellerCountryDetails.scss';
import classNames from 'classnames';
import { format } from 'date-fns';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import CountryLabel from '../../../components/CountryLabel/CountryLabel';
import { Loader } from '../../../components/Loader/Loader';
import {
  getInProgressClientVATIds,
  getRegisteredVATIds,
} from '../../../core/api';
import { useAppSelector, useQueriesTyped } from '../../../hooks';
import { IdNumberStatusEnum } from '../SellerVATIds/SellerVATIds';
import OngoingFilingTab from './OngoingFilingTab';
import FilingArchiveTab from './FilingArchiveTab';
import Datev from '../SellerDatev/SellerDatev';
import Proforma from '../../TaxAdvisor/Proforma/Proforma';
import Tabs from '../../../components/Tabs/Tabs';

const TABS = ['Ongoing filing', 'Filing archive', 'DATEV', 'Pro-Forma'];

export default function SellerCountryDetails() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const user = useAppSelector((s) => s.user.user);
  const activeCompanyId = useAppSelector(
    (s) => s.user.activeCompany?.company_id
  );

  const history = useHistory();

  const [registrations, registeredVATIds] = useQueriesTyped([
    {
      queryKey: ['SellerVATIds', user!.Id, activeCompanyId],
      queryFn: () => getInProgressClientVATIds(activeCompanyId),
    },
    {
      queryKey: ['SellerRegisteredVATIds', user!.Id, activeCompanyId],
      queryFn: () => getRegisteredVATIds(activeCompanyId),
    },
  ]);

  const isLoading = registrations.isLoading || registeredVATIds.isLoading;

  const registrationsData = registrations.data;
  const registeredVATIdsData = registeredVATIds.data;

  const vatIds = useMemo(
    () => [...(registrationsData || []), ...(registeredVATIdsData || [])],
    [registrationsData, registeredVATIdsData]
  );
  const vatId = vatIds.find((v) => v.Country.short_name === id);

  useEffect(() => {
    if (!isLoading && !vatId) {
      history.push('/404');
    }
  }, [isLoading, vatId, history]);

  const renderContent = (activeTab: string | number) => {
    switch (activeTab) {
      case 'Filing archive':
        return <FilingArchiveTab />;
      case 'DATEV':
        return <Datev page={false} />;
      case 'Pro-Forma':
        return <Proforma page={false} />;
      case 'Ongoing filing':
      default:
        return <OngoingFilingTab />;
    }
  };

  return (
    <div className='SellerCountryDetails flex-grow-1 p-5'>
      {isLoading ? (
        <Loader />
      ) : (
        vatId && (
          <div className='container'>
            <div className='Paper Paper--sm mx-auto p-4'>
              <CountryLabel
                country={id}
                className='CountryLabel--SellerCountryDetails mb-4'
              />
              <div className='Text--20 fw-800 mb-2'>{t('General details')}</div>
              <div className='row row-cols-4 mb-3'>
                <div className='col'>
                  <div className='fw-600'>{t('Contract')}</div>
                  {format(new Date(2020, 0, 12), 'dd.MM.yyyy')}
                </div>
                <div className='col'>
                  <div className='fw-600'>{t('Registration')}</div>
                  {format(new Date(2020, 0, 12), 'dd.MM.yyyy')}
                </div>
                <div className='col'>
                  <div className='fw-600'>{t('Reason')}</div>
                  Threshold
                </div>
                <div className='col'>
                  <div className='fw-600'>{t('Tax ID')}</div>
                  <Cell
                    number={vatId.tax_number}
                    status={vatId.tax_number_status}
                    date={vatId.start_date_tax_number}
                  />
                </div>
              </div>
              <div className='row row-cols-4 mb-4 pb-2'>
                <div className='col'>
                  <div className='fw-600'>{t('Filing by countX')}</div>
                  Since May 2020
                </div>
                <div className='col'>
                  <div className='fw-600'>{t('Frequency')}</div>
                  Quarterly
                </div>
                <div className='col'>
                  <div className='fw-600'>{t('Warehouses')}</div>
                  Yes
                </div>
                <div className='col'>
                  <div className='fw-600'>{t('Tax ID')}</div>
                  <Cell
                    number={vatId.vat_number}
                    status={vatId.vat_number_status}
                    date={vatId.start_date_vat_number}
                  />
                </div>
              </div>
              <div className='Text--20 fw-800'>{t('Related files')}</div>
              <Tabs tabs={TABS} renderContent={renderContent} />
            </div>
          </div>
        )
      )}
    </div>
  );
}

const Cell = ({
  number,
  status,
  date,
}: {
  number: string;
  status?: IdNumberStatusEnum;
  date: string;
}) => {
  const { t } = useTranslation();
  return (
    <>
      {number}
      <div className='d-flex align-items-center Text--sm'>
        <div
          className={classNames({
            'Text--green': status === IdNumberStatusEnum.Active,
            'Text--gray': status !== IdNumberStatusEnum.Active,
          })}
        >
          {typeof status === 'number' && t(IdNumberStatusEnum[status])}
        </div>
        &nbsp;
        {date && status === IdNumberStatusEnum.Active && (
          <div>
            • &nbsp;
            {format(new Date(date), 'dd.MM.yyyy')}
          </div>
        )}
      </div>
    </>
  );
};
